import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { del, get, patch, post } from "../../lib/request"
import {
  buildInboxStreamUpdateBody,
  isCustomStreamTypeId,
  normalizeInboxStreamRows,
} from "../../lib/inbox-stream-api"

export const fetchInboxStreams = createAsyncThunk(
  "inboxStream/fetch",
  async ({ clientId } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      const data = await get(`inbox-stream/clients/${clientId}`)
      return {
        rows: normalizeInboxStreamRows(data, clientId),
        clientId: String(clientId),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch inbox streams")
    }
  },
)

function pickSavedInboxStreamRow(rows, { streamId, streamTypeId, typeId, body }) {
  if (streamId != null) {
    return (
      rows.find((r) => r.streamId === streamId || r.recordId === streamId) ??
      rows[0] ??
      null
    )
  }

  const resolvedTypeId = Number(streamTypeId ?? typeId)
  if (!Number.isFinite(resolvedTypeId)) return rows[0] ?? null

  const textValue = String(body?.search || body?.hashtag || body?.location || "").trim()

  if (isCustomStreamTypeId(resolvedTypeId) && textValue) {
    return (
      rows.find(
        (r) =>
          r.typeId === resolvedTypeId &&
          String(r.streamName || r.name || "").trim() === textValue,
      ) ??
      rows.find((r) => r.typeId === resolvedTypeId) ??
      rows[0] ??
      null
    )
  }

  return rows.find((r) => r.typeId === resolvedTypeId) ?? rows[0] ?? null
}

export const createInboxStream = createAsyncThunk(
  "inboxStream/create",
  async ({ clientId, body } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (body?.streamTypeId == null) {
        return rejectWithValue("streamTypeId is required")
      }

      const data = await post(`inbox-stream/clients/${clientId}`, body)
      const rows = normalizeInboxStreamRows(data, clientId)
      return {
        clientId: String(clientId),
        row: pickSavedInboxStreamRow(rows, { streamTypeId: body.streamTypeId, body }),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create inbox stream")
    }
  },
)

export const updateInboxStream = createAsyncThunk(
  "inboxStream/update",
  async (
    {
      clientId,
      streamId,
      id,
      streamTypeId,
      typeId,
      flag,
      name,
      isChecked,
      search,
      hashtag,
      location,
      body: bodyOverride,
    } = {},
    { rejectWithValue },
  ) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      const patchId = streamId ?? id
      if (patchId == null || String(patchId).trim() === "") {
        return rejectWithValue("streamId is required")
      }

      const body =
        bodyOverride ??
        buildInboxStreamUpdateBody({
          streamTypeId: streamTypeId ?? typeId,
          typeId,
          flag,
          name,
          isChecked,
          search,
          hashtag,
          location,
        })

      const data = await patch(`inbox-stream/clients/${clientId}/${patchId}`, body)
      const rows = normalizeInboxStreamRows(data, clientId)
      return {
        clientId: String(clientId),
        row:
          pickSavedInboxStreamRow(rows, {
            streamId: patchId,
            streamTypeId: body.streamTypeId,
            typeId: body.streamTypeId,
            body,
          }) ??
          rows.find((r) => r.streamId === patchId || r.recordId === patchId) ??
          null,
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update inbox stream")
    }
  },
)

/** @deprecated Use createInboxStream or updateInboxStream. */
export const saveInboxStream = createInboxStream

export const deleteInboxStream = createAsyncThunk(
  "inboxStream/delete",
  async ({ clientId, subscriptionId } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (subscriptionId == null || String(subscriptionId).trim() === "") {
        return rejectWithValue("subscriptionId is required")
      }

      await del(`inbox-stream/clients/${clientId}/subscriptions/${subscriptionId}`)
      return {
        clientId: String(clientId),
        subscriptionId: String(subscriptionId),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete inbox stream")
    }
  },
)

const initialState = {
  rows: [],
  status: "idle",
  saveStatus: "idle",
  error: null,
  saveError: null,
  requestedClientId: null,
  fetchedClientId: null,
}

function beginFetch(state, clientId) {
  state.status = "loading"
  state.error = null
  state.requestedClientId = String(clientId)
  state.fetchedClientId = null
  state.rows = []
}

function isStaleClient(action, state) {
  return String(action.meta.arg?.clientId ?? "") !== String(state.requestedClientId ?? "")
}

function mergeSavedInboxStreamRow(state, row) {
  if (!row) return
  const idx = state.rows.findIndex(
    (r) =>
      (row.streamId != null && r.streamId === row.streamId) ||
      (row.recordId != null && r.recordId === row.recordId) ||
      (row.subscriptionId != null && r.subscriptionId === row.subscriptionId) ||
      (isCustomStreamTypeId(row.typeId) &&
        r.typeId === row.typeId &&
        String(r.streamName || r.name || "") ===
          String(row.streamName || row.name || "")) ||
      (!isCustomStreamTypeId(row.typeId) && r.typeId === row.typeId),
  )
  if (idx >= 0) state.rows[idx] = { ...state.rows[idx], ...row }
  else state.rows.push(row)
}

const inboxStreamSlice = createSlice({
  name: "inboxStream",
  initialState,
  reducers: {
    clearInboxStreams: () => initialState,
    clearInboxStreamSaveError: (state) => {
      state.saveError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInboxStreams.pending, (state, action) => {
        const nextId =
          action.meta.arg?.clientId != null ? String(action.meta.arg.clientId) : null
        if (nextId == null) return

        const prevId =
          state.fetchedClientId != null ? String(state.fetchedClientId) : null
        const isSameClientRefresh =
          prevId !== null && prevId === nextId && state.status === "succeeded"

        if (isSameClientRefresh) {
          state.requestedClientId = nextId
          state.error = null
          return
        }

        beginFetch(state, nextId)
      })
      .addCase(fetchInboxStreams.fulfilled, (state, action) => {
        if (isStaleClient(action, state)) return
        state.status = "succeeded"
        state.rows = action.payload?.rows ?? []
        state.fetchedClientId = action.payload?.clientId ?? null
      })
      .addCase(fetchInboxStreams.rejected, (state, action) => {
        if (isStaleClient(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch inbox streams"
        state.rows = []
        state.fetchedClientId = state.requestedClientId
      })
      .addCase(createInboxStream.pending, (state) => {
        state.saveStatus = "loading"
        state.saveError = null
      })
      .addCase(createInboxStream.fulfilled, (state, action) => {
        state.saveStatus = "succeeded"
        mergeSavedInboxStreamRow(state, action.payload?.row)
      })
      .addCase(createInboxStream.rejected, (state, action) => {
        state.saveStatus = "failed"
        state.saveError = action.payload ?? "Failed to create inbox stream"
      })
      .addCase(updateInboxStream.pending, (state) => {
        state.saveStatus = "loading"
        state.saveError = null
      })
      .addCase(updateInboxStream.fulfilled, (state, action) => {
        state.saveStatus = "succeeded"
        mergeSavedInboxStreamRow(state, action.payload?.row)
      })
      .addCase(updateInboxStream.rejected, (state, action) => {
        state.saveStatus = "failed"
        state.saveError = action.payload ?? "Failed to update inbox stream"
      })
      .addCase(deleteInboxStream.pending, (state) => {
        state.saveStatus = "loading"
        state.saveError = null
      })
      .addCase(deleteInboxStream.fulfilled, (state, action) => {
        state.saveStatus = "succeeded"
        const subscriptionId = action.payload?.subscriptionId
        if (subscriptionId == null) return
        state.rows = state.rows.filter(
          (r) => String(r.subscriptionId ?? "") !== String(subscriptionId),
        )
      })
      .addCase(deleteInboxStream.rejected, (state, action) => {
        state.saveStatus = "failed"
        state.saveError = action.payload ?? "Failed to delete inbox stream"
      })
  },
})

export const { clearInboxStreams, clearInboxStreamSaveError } = inboxStreamSlice.actions
export default inboxStreamSlice.reducer
